import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button, Card, Col, Form as BsForm, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import {
  preVisualizarAlteraNomeAtivo,
  executarAlteraNomeAtivo,
} from '~/Services/Service';

const extrairMensagemErro = (err) =>
  (err && err.response && err.response.data && err.response.data.message) ||
  (err && err.response && err.response.data) ||
  (err && err.message) ||
  'Erro ao processar. Tente novamente.';

const schema = Yup.object().shape({
  texto_busca: Yup.string()
    .required('Informe o texto a pesquisar.')
    .min(1, 'Informe o texto a pesquisar.'),
  texto_substituto: Yup.string().nullable().default(''),
});

const colunasPreview = [
  { title: 'Código', key: 'codigo_ativo', ordenacao: true, width: '120px' },
  { title: 'Nome Atual', key: 'nome_anterior', ordenacao: true },
  { title: 'Nome Resultante', key: 'nome_novo', ordenacao: true },
];

const AlteraNomeAtivo = () => {
  const [previewData, setPreviewData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExecucao, setLoadingExecucao] = useState(false);
  const [showConfirmacao, setShowConfirmacao] = useState(false);
  const [valoresAtuais, setValoresAtuais] = useState({
    texto_busca: '',
    texto_substituto: '',
  });

  const dadosTabela = previewData.map((item) => ({
    ...item,
    id: item.codigo_ativo,
  }));

  const handlePreview = async (values, { setSubmitting }) => {
    setLoadingPreview(true);
    setPreviewData([]);
    try {
      const payload = {
        texto_busca: values.texto_busca,
        texto_substituto: values.texto_substituto != null ? values.texto_substituto : '',
      };
      const response = await preVisualizarAlteraNomeAtivo(payload);
      const lista = response && response.preview ? response.preview : [];
      setValoresAtuais(payload);
      setPreviewData(lista);
      if (lista.length === 0) {
        toast.info('Nenhum ativo encontrado com o texto informado.');
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingPreview(false);
      setSubmitting(false);
    }
  };

  const handleLimpar = (resetForm) => {
    resetForm({ values: { texto_busca: '', texto_substituto: '' } });
    setPreviewData([]);
    setValoresAtuais({ texto_busca: '', texto_substituto: '' });
  };

  const handleConfirmarClick = () => {
    setShowConfirmacao(true);
  };

  const handleConfirmacao = async (confirmado) => {
    setShowConfirmacao(false);
    if (!confirmado) return;

    setLoadingExecucao(true);
    try {
      const response = await executarAlteraNomeAtivo(valoresAtuais);
      const total = response && response.ativos_atualizados != null
        ? response.ativos_atualizados
        : 0;
      toast.success(`${total} ativo(s) alterado(s) com sucesso.`);
      setPreviewData([]);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingExecucao(false);
    }
  };

  return (
    <div>
      <Header title="Altera Nome Ativo" />

      <div className="container-fluid py-3">
        <Card className="mb-3">
          <Card.Header>
            <strong>Pesquisar e Substituir</strong>
          </Card.Header>
          <Card.Body>
            <Formik
              initialValues={{ texto_busca: '', texto_substituto: '' }}
              validationSchema={schema}
              onSubmit={handlePreview}
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting, resetForm }) => (
                <Form>
                  <Row>
                    <Col xs={12} md={6} className="mb-3">
                      <BsForm.Group controlId="texto_busca">
                        <small>Texto a pesquisar</small>
                        <BsForm.Control
                          type="text"
                          name="texto_busca"
                          value={values.texto_busca}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={!!(errors.texto_busca && touched.texto_busca)}
                          maxLength={30}
                          autoComplete="off"
                        />
                        <BsForm.Control.Feedback type="invalid">
                          {errors.texto_busca}
                        </BsForm.Control.Feedback>
                      </BsForm.Group>
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                      <BsForm.Group controlId="texto_substituto">
                        <small>Substituir por (vazio = remover)</small>
                        <BsForm.Control
                          type="text"
                          name="texto_substituto"
                          value={values.texto_substituto}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          maxLength={30}
                          autoComplete="off"
                        />
                      </BsForm.Group>
                    </Col>
                  </Row>

                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={isSubmitting || loadingPreview}
                    >
                      {loadingPreview ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Pré-visualizando...
                        </>
                      ) : (
                        'Pré-visualizar'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleLimpar(resetForm)}
                      disabled={loadingPreview || loadingExecucao}
                    >
                      Limpar
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>

        {previewData.length > 0 && (
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <strong>
                Pré-visualização ({previewData.length} ativo{previewData.length === 1 ? '' : 's'})
              </strong>
              <Button
                variant="success"
                size="sm"
                onClick={handleConfirmarClick}
                disabled={loadingExecucao || previewData.length === 0}
              >
                {loadingExecucao ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Aplicando...
                  </>
                ) : (
                  'Confirmar Alteração'
                )}
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <TabelaDinamica
                colunas={colunasPreview}
                dados={dadosTabela}
                striped
                hover
              />
            </Card.Body>
          </Card>
        )}

        <ModalConfirmacao
          show={showConfirmacao}
          onClose={handleConfirmacao}
          title="Confirmar Alteração"
          message={`Confirma a alteração dos nomes de ${previewData.length} ativo(s)?`}
          variant="danger"
        />
      </div>
    </div>
  );
};

export default AlteraNomeAtivo;
